import { actions } from "../../app/state.js";
import { listAllEvents, setEventStage } from "../../services/eventsService.js";
import { createPayment, getDepositStatus } from "../../services/paymentsService.js";
import { getCurrentTeamId } from "../../services/teamService.js";
import { onSafeBackdropClick } from "../modalUtils.js";

function $(root, selector) {
  return root.querySelector(selector);
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

function normStr(value) {
  return String(value ?? "").trim();
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normalizeSearchText(value) {
  return normStr(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function eventDisplayName(event) {
  return event?.client_name_legacy || event?.name || "Evento sin nombre";
}

function eventMeta(event) {
  return [event?.date || "Sin fecha", event?.primary_venue_name || event?.location || event?.city, event?.stage]
    .filter(Boolean)
    .join(" - ");
}

function eventSearchHaystack(event) {
  return normalizeSearchText([
    eventDisplayName(event),
    event?.date,
    event?.stage,
    event?.primary_venue_name,
    event?.location,
    event?.city,
    event?.region,
  ].filter(Boolean).join(" "));
}

function buildModal() {
  const modal = document.createElement("div");
  modal.className = "modal-backdrop mobile-payment-modal";
  modal.innerHTML = `
    <div class="modal-dialog">
      <div class="modal-header">
        <h3 class="text-lg font-bold text-slate-900">Registrar ingreso</h3>
        <p class="text-sm text-slate-500">Anota una senal, pago final u otro cobro de un evento.</p>
      </div>
      <div class="modal-body">
        <div class="space-y-3">
          <div>
            <label class="block text-sm text-slate-600 mb-1">Evento *</label>
            <select id="pay-modal-event-id" class="hidden"></select>
            <div class="relative">
              <input id="pay-modal-event-search" type="text" autocomplete="off"
                class="w-full px-3 py-2 pl-9 rounded-xl border border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="Buscar evento por nombre, fecha o lugar..." />
              <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
              <div id="pay-modal-event-results"
                class="hidden absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg custom-scrollbar"></div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm text-slate-600 mb-1">Importe (EUR) *</label>
              <input id="pay-modal-amount" type="number" step="0.01" inputmode="decimal"
                class="w-full px-3 py-2 rounded-xl border border-slate-200" placeholder="0.00">
            </div>
            <div>
              <label class="block text-sm text-slate-600 mb-1">Fecha</label>
              <input id="pay-modal-date" type="date" class="w-full px-3 py-2 rounded-xl border border-slate-200">
            </div>
          </div>

          <div>
            <label class="block text-sm text-slate-600 mb-1">Tipo</label>
            <select id="pay-modal-type" class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white">
              <option value="advance">Senal</option>
              <option value="final">Pago final</option>
              <option value="other">Otro ingreso</option>
            </select>
          </div>

          <div>
            <label class="block text-sm text-slate-600 mb-1">Nota</label>
            <input id="pay-modal-note" type="text" class="w-full px-3 py-2 rounded-xl border border-slate-200"
              placeholder="Ej: transferencia, efectivo, Bizum...">
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button id="pay-modal-cancel" class="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-medium">Cancelar</button>
        <button id="pay-modal-save" class="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-medium">Guardar ingreso</button>
      </div>
    </div>
  `;
  return modal;
}

function setupEventSearch(modal, events, selectedEventId) {
  const select = $(modal, "#pay-modal-event-id");
  const input = $(modal, "#pay-modal-event-search");
  const results = $(modal, "#pay-modal-event-results");
  if (!select || !input || !results) return;

  const today = todayISO();
  const allowedEvents = (events || [])
    .filter(event => event.id === selectedEventId
      || (normStr(event.stage) === "confirmed" && normStr(event.date).slice(0, 10) >= today))
    .sort((a, b) => normStr(a.date).localeCompare(normStr(b.date)));

  select.innerHTML = '<option value=""></option>';
  allowedEvents.forEach(event => {
    const option = document.createElement("option");
    option.value = event.id;
    option.textContent = eventDisplayName(event);
    select.appendChild(option);
  });

  const selected = allowedEvents.find(event => event.id === selectedEventId);
  if (selected) {
    select.value = selected.id;
    input.value = `${eventDisplayName(selected)} (${selected.date || "Sin fecha"})`;
  }

  function closeResults() {
    results.classList.add("hidden");
  }

  function selectEvent(event) {
    select.value = event?.id || "";
    input.value = event ? `${eventDisplayName(event)} (${event.date || "Sin fecha"})` : "";
    closeResults();
  }

  function render(query = "") {
    const normalized = normalizeSearchText(query);
    const matches = (normalized
      ? allowedEvents.filter(event => eventSearchHaystack(event).includes(normalized))
      : allowedEvents
    ).slice(0, 30);

    results.innerHTML = matches.length
      ? matches.map(event => `
          <button type="button" data-payment-event-id="${escapeHtml(event.id)}"
            class="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0">
            <span class="block text-sm font-bold text-slate-900 truncate">${escapeHtml(eventDisplayName(event))}</span>
            <span class="block text-xs text-slate-500 truncate">${escapeHtml(eventMeta(event))}</span>
          </button>
        `).join("")
      : '<div class="px-3 py-4 text-sm text-slate-500">No hay eventos para esa busqueda.</div>';
    results.classList.remove("hidden");
  }

  input.addEventListener("focus", () => render(input.value));
  input.addEventListener("input", () => {
    select.value = "";
    render(input.value);
  });
  results.addEventListener("mousedown", (event) => {
    event.preventDefault();
    const btn = event.target.closest("[data-payment-event-id]");
    if (!btn) return;
    selectEvent(allowedEvents.find(row => row.id === btn.dataset.paymentEventId));
  });
  document.addEventListener("mousedown", (event) => {
    if (!input.parentElement.contains(event.target)) closeResults();
  });
}

async function openCreatePaymentModal({ event_id = null, onSuccess = null } = {}) {
  const modal = buildModal();
  document.body.appendChild(modal);

  const dateInput = $(modal, "#pay-modal-date");
  const saveBtn = $(modal, "#pay-modal-save");
  const cancelBtn = $(modal, "#pay-modal-cancel");
  if (dateInput) dateInput.value = todayISO();

  let events = [];
  try {
    events = await listAllEvents();
    setupEventSearch(modal, events, event_id);
  } catch (error) {
    console.error("[PaymentModal] Error loading events", error);
    actions.addToast?.({ type: "error", message: "No se pudieron cargar los eventos" });
  }

  const close = () => modal.remove();
  cancelBtn?.addEventListener("click", close);
  onSafeBackdropClick(modal, close);

  saveBtn?.addEventListener("click", async () => {
    const eventId = $(modal, "#pay-modal-event-id")?.value || "";
    const amount = Number($(modal, "#pay-modal-amount")?.value);
    const paymentType = $(modal, "#pay-modal-type")?.value || "advance";
    const noteInput = normStr($(modal, "#pay-modal-note")?.value);
    const selectedEvent = events.find(event => event.id === eventId);

    if (!eventId) {
      actions.addToast?.({ type: "error", message: "Selecciona un evento" });
      return;
    }
    if (!amount || amount <= 0) {
      actions.addToast?.({ type: "error", message: "Introduce un importe valido" });
      return;
    }

    try {
      saveBtn.disabled = true;
      saveBtn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin mr-2"></i>Guardando...';
      await createPayment({
        team_id: selectedEvent?.team_id || await getCurrentTeamId(),
        event_id: eventId,
        amount,
        payment_date: $(modal, "#pay-modal-date")?.value || todayISO(),
        payment_type: paymentType,
        note: noteInput || (paymentType === "advance" ? "Senal" : "Pago"),
      });

      // Pasa a Confirmado cuando la señal registrada, con este ingreso, cubre
      // la pactada (porcentaje del precio), no al llegar a una cifra fija.
      if (paymentType === "advance" && selectedEvent?.stage === "accepted_pending_deposit"
        && (await getDepositStatus(selectedEvent)).covered) {
        await setEventStage(eventId, "confirmed");
      }

      actions.addToast?.({ type: "success", message: "Ingreso registrado correctamente" });
      close();
      if (typeof onSuccess === "function") await onSuccess();
    } catch (error) {
      console.error("[PaymentModal] Error saving payment", error);
      actions.addToast?.({ type: "error", message: "Error al registrar el ingreso: " + error.message });
      saveBtn.disabled = false;
      saveBtn.innerHTML = "Guardar ingreso";
    }
  });

  return modal;
}

export default {
  openCreatePaymentModal,
};
